import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Stack,
  LinearProgress,
  Alert,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  setApplicationStep,
  setUkAssessmentResult,
  resetUkAssessment,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import api from "src/store/features/api";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const RETRY_WAIT_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Countdown page for the mandatory 24-hour wait after a second failed
 * assessment attempt, mirroring the cooling-off countdown interface. Once the
 * timer lapses the user can start their final attempt.
 */
export default function UKAssessmentWaitPending() {
  const dispatch = useDispatch();
  const { ukOnboarding } = useOnboarding();
  const { assessment } = ukOnboarding;

  const [endsAt, setEndsAt] = useState<number | null>(assessment.nextAttemptAvailableAt);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [getAssessmentStatus] = api.useLazyGetAssessmentStatusQuery();

  // The slice value covers the just-failed submission; re-fetch the status so
  // a returning user (fresh session) gets the authoritative wait end time.
  useEffect(() => {
    initializeWaitPeriod();
  }, []);

  useEffect(() => {
    if (!endsAt) return;

    const timer = setInterval(() => {
      const remaining = endsAt - Date.now();

      if (remaining <= 0) {
        setIsComplete(true);
        setTimeRemaining(0);
        clearInterval(timer);
      } else {
        setTimeRemaining(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endsAt]);

  const initializeWaitPeriod = async () => {
    try {
      const status = await getAssessmentStatus().unwrap();

      if (status.nextAttemptAvailableAt && status.nextAttemptAvailableAt > Date.now()) {
        setEndsAt(status.nextAttemptAvailableAt);
        dispatch(
          setUkAssessmentResult({
            status: "failed",
            attemptNumber: status.attemptCount,
            nextAttemptAvailableAt: status.nextAttemptAvailableAt,
          }),
        );
        return;
      }

      // No active wait on the backend — the period has already lapsed
      setIsComplete(true);
    } catch (err: any) {
      // Fall back to the slice value from the just-submitted attempt
      if (endsAt && endsAt > Date.now()) return;
      setError(err?.data?.message || "Failed to load your assessment status.");
    }
  };

  const formatTimeRemaining = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const progress =
    timeRemaining !== null
      ? ((RETRY_WAIT_DURATION_MS - timeRemaining) / RETRY_WAIT_DURATION_MS) * 100
      : 0;

  const handleStartFinalAttempt = () => {
    dispatch(resetUkAssessment());
    dispatch(setApplicationStep("ukAssessment"));
  };

  const handleExit = () => {
    window.location.href = "/";
  };

  if (error) {
    return (
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isComplete) {
    return (
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper
            elevation={1}
            sx={{
              p: 4,
              borderRadius: 2,
              textAlign: "center",
              backgroundColor: "#E8F5E9",
              border: "2px solid #4CAF50",
            }}
          >
            <Typography variant="h4" fontWeight={600} gutterBottom color="#2E7D32">
              Cooling-Off Period Complete
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              You can now make your final attempt at the assessment. You have{" "}
              <strong>one attempt remaining</strong> to successfully complete the test.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="center" mt={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleStartFinalAttempt}
              sx={{ minWidth: 300 }}
            >
              Start Final Attempt
            </Button>
          </Stack>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} mt={2}>
      <Grid item xs={12}>
        <Paper
          elevation={1}
          sx={{
            p: 4,
            borderRadius: 2,
            textAlign: "center",
            backgroundColor: "#E3F2FD",
            border: "1px solid #90CAF9",
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 60, color: "#1976d2", mb: 2 }} />
          <Typography variant="h4" fontWeight={600} gutterBottom>
            24-Hour Cooling-Off Period
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            As required by UK regulations, a mandatory 24-hour cooling off period applies
            before you can attempt the test again. Once the time period has lapsed, you will
            have <strong>one attempt remaining to successfully complete the test.</strong>
          </Typography>

          {timeRemaining !== null && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h2" fontWeight={700} color="primary" gutterBottom>
                {formatTimeRemaining(timeRemaining)}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Time Remaining
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 10, borderRadius: 5, mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress)}% Complete
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <InfoOutlinedIcon sx={{ color: "#1976d2", mt: 0.5 }} />
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Why is there a cooling-off period?
              </Typography>
              <Stack spacing={2}>
                <Typography variant="body1">
                  The cooling-off period is designed to protect you by giving you time to:
                </Typography>
                <Stack spacing={1} ml={2}>
                  <Typography variant="body2">
                    • Reflect on your decision to invest in cryptoassets
                  </Typography>
                  <Typography variant="body2">
                    • Consider the risks involved with cryptoasset investments
                  </Typography>
                  <Typography variant="body2">
                    • Ensure you are not acting impulsively
                  </Typography>
                  <Typography variant="body2">
                    • Review the information provided during the application process
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Can I leave this page?</strong>
            <br />
            Yes, you can safely close this page. The cooling-off timer will continue in the
            background, and you can return to make your final attempt once the period is
            complete.
          </Typography>
        </Alert>
      </Grid>

      <Grid item xs={12} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Stack direction="row" justifyContent="center" mt={2}>
          <Button variant="outlined" color="inherit" size="large" onClick={handleExit}>
            Exit Application
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
}
